﻿import { Box, Button, Container, Paper, Typography } from "@mui/material";
import CatalogMappingGrids from "./CatalogMappingGrids";
import { useEffect, useRef, useState } from "react";
import {
  clearPendingLocalChanges,
  saveNewMappings,
  selectMappedSourceCatalogs,
  selectMapOnFlyInfo,
  setIsMapOnFlyOpen,
  setMapOnFlyInfo,
  selectIsAddCrossRefOpen,
  resetCatalogFilter,
  selectConnectionCatalogs,
  selectMissingCrossRefCounts,
  saveNewCrossReferenceMappings,
  setSelectedXrefType,
  fetchSourceConnectionCatalogs,
  fetchTargetConnectionCatalogs,
  fetchMappedCatalogs,
  selectSourceGridLoaded,
  selectTargetGridLoaded,
} from "@/features/connections/crossReferenceSlice";
import { useDispatch, useSelector } from "react-redux";
import MappedCrossReferencesGrid from "./MappedCrossReferencesGrid";
import { selectWorkflow, statusTypes, statusLabels } from "@/features/homepage/workflowManagerSessionSlice";
import { unwrapResult } from "@reduxjs/toolkit";
import axios from "@/features/axiosHelper";
import { createAlert } from "@/features/homepage/alertSliceList";
import { fetchCrossReferenceSummaries, selectCrossReferenceSummarySelectedTab, clearTabCache } from "@/features/maintenance/maintenanceSlice";
import { getRequeueStatus } from "@/components/Maintenance/MapOnFly/MapOnFly";
import {
  setIsWorkflowStepsOpen,
  setWorkflowStepsInfo,
  updateWorkflowStatus,
  fetchMissingCrossReferenceData,
} from "@/features/homepage/workflowManagerSlice";
import DetailDisplay from "@/components/Maintenance/DetailDisplay";

const ItemCatalogTab = () => {
  const dispatch = useDispatch();
  const mapOnFlyInfo = useSelector(selectMapOnFlyInfo);
  const workflow = useSelector(selectWorkflow);
  const mappedCatalogs = useSelector(selectMappedSourceCatalogs);
  const unmappedCatalogCounts = useSelector(selectMissingCrossRefCounts);
  const isAddCrossRef = useSelector(selectIsAddCrossRefOpen);
  const catalogInformation = useSelector(selectConnectionCatalogs);
  const selectedTab = useSelector(selectCrossReferenceSummarySelectedTab);
  const sourceGridLoaded = useSelector(selectSourceGridLoaded);
  const targetGridLoaded = useSelector(selectTargetGridLoaded);

  const identifiedCatalogs = isAddCrossRef
    ? null
    : mapOnFlyInfo?.crossReferenceInfo.IdentifiedCatalogs.filter(
        (x) => x.ReferenceType === 1
      );

  const [canRequeue, setCanRequeue] = useState(false);

  const [sourceSearchValue, setSourceSearchValue] = useState("");
  const [targetSearchValue, setTargetSearchValue] = useState("");

  const prevMapOnFlyInfo = useRef(null);
  useEffect(() => {
    const prev = JSON.stringify(prevMapOnFlyInfo.current);
    const current = JSON.stringify(mapOnFlyInfo);

    if (prev !== current) {
      fetchCatalogs();
      dispatch(resetCatalogFilter({}));
      prevMapOnFlyInfo.current = mapOnFlyInfo;
    }
  }, [mapOnFlyInfo]);

  useEffect(() => {
    if (isAddCrossRef) {
      setCanRequeue(false);
    } else {
      setCanRequeue(() =>
        getRequeueStatus({
          unmappedCatalogCounts,
          referenceType: "Item",
          catalogInformation,
          identifiedCatalogs,
        })
      );
    }
  }, [unmappedCatalogCounts, catalogInformation, mapOnFlyInfo, mappedCatalogs]);

  const fetchCatalogs = () => {
    const info = mapOnFlyInfo.crossReferenceInfo;
    dispatch(setSelectedXrefType({ selectedXref: "Item" }));
    // mapped first, prevents duplicates in source grid
    dispatch(
      fetchMappedCatalogs({
        connectionPairId: info.ConnectionPairId,
        crossReferenceType: "Item",
        sourceConnectionId: info.SourceConnectionId,
        targetConnectionId: info.TargetConnectionId,
      })
    ).then(() => {
      dispatch(
        fetchSourceConnectionCatalogs({
          connectionPairId: info.ConnectionPairId,
          crossReferenceType: "Item",
          sourceConnectionId: info.SourceConnectionId,
          targetConnectionId: info.TargetConnectionId,
          searchFilter: sourceSearchValue,
        })
      );
      dispatch(
        fetchTargetConnectionCatalogs({
          connectionPairId: info.ConnectionPairId,
          crossReferenceType: "Item",
          sourceConnectionId: info.SourceConnectionId,
          targetConnectionId: info.TargetConnectionId,
          searchFilter: targetSearchValue,
        })
      );
    });
  };

  const closeWorkflowStepsPopup = () => {
    dispatch(
      setWorkflowStepsInfo({
        workflowRecordId: "",
        sourceArtifactDictionaryDataId: "",
        connectionPairId: -1,
      })
    );
    dispatch(setIsWorkflowStepsOpen({ isOpen: false }));
  };

  const handleSave = () => {
    // heap.io
    if (isAddCrossRef) {
      window.heap.track("Cross References - Create New");
    } else {
      window.heap.track("Map on Fly - Save New Cross References");
    }

    dispatch(
      isAddCrossRef
        ? saveNewCrossReferenceMappings({
            connectionPairId: mapOnFlyInfo.crossReferenceInfo.ConnectionPairId,
            crossReferenceType: "Item",
            sourceConnectionId:
              mapOnFlyInfo.crossReferenceInfo.SourceConnectionId,
            targetConnectionId:
              mapOnFlyInfo.crossReferenceInfo.TargetConnectionId,
            newMappings: mappedCatalogs,
          })
        : saveNewMappings({
            connectionPairId: mapOnFlyInfo.crossReferenceInfo.ConnectionPairId,
            crossReferenceType: "Item",
            sourceConnectionId:
              mapOnFlyInfo.crossReferenceInfo.SourceConnectionId,
            targetConnectionId:
              mapOnFlyInfo.crossReferenceInfo.TargetConnectionId,
            newMappings: mappedCatalogs,
            usedCatalogs: identifiedCatalogs,
          })
    )
      .then(unwrapResult)
      .then((response) => {
        dispatch(
          createAlert({
            status: response.data.error ? "Error" : "Success",
            message: response.data.message,
            alertType: "single",
          })
        );
      })
      .then(() => {
        if (isAddCrossRef) {
          fetchCatalogs();
          dispatch(clearTabCache(selectedTab));
          dispatch(
            fetchCrossReferenceSummaries({
              connectionPairId:
                mapOnFlyInfo.crossReferenceInfo.ConnectionPairId,
              crossReferenceType: selectedTab,
              })
          );
        } else {
          dispatch(
            fetchMissingCrossReferenceData({ currentOperationId: workflow })
          );
        }
      });
  };
  const getStatusLabel = (statusValue) => {
    const status = statusLabels[statusValue];
    return status ? status.label : "Unknown";
  };
  const handleSaveAndRequeue = () => {
    // heap.io
    window.heap.track("Map on Fly - Save and Requeue");

    dispatch(
      saveNewMappings({
        connectionPairId: mapOnFlyInfo.crossReferenceInfo.ConnectionPairId,
        crossReferenceType: "Item",
        sourceConnectionId: mapOnFlyInfo.crossReferenceInfo.SourceConnectionId,
        targetConnectionId: mapOnFlyInfo.crossReferenceInfo.TargetConnectionId,
        newMappings: mappedCatalogs,
        usedCatalogs: identifiedCatalogs,
      })
    )
      .then(unwrapResult)
      .then((response) => {
        const workflowRowActionDTO = [
          {
            SourceDictionaryDataId:
              mapOnFlyInfo.crossReferenceInfo.SourceArtifactDictionaryDataId,
            Status: statusTypes.Error,
            IsHeld: false,
            IsArchived: false,
            SourceReference: mapOnFlyInfo.crossReferenceInfo.ReferenceId,
            OperationId: workflow,
            SourceConnectionId:
              mapOnFlyInfo.crossReferenceInfo.SourceConnectionId,
            ConnPairId: mapOnFlyInfo.crossReferenceInfo.ConnectionPairId,
            WorkflowRecordId: mapOnFlyInfo.crossReferenceWorkflowRecordId,
          },
        ];
        axios
          .post(
            "/operationsmanager/requeue-operation-task",
            workflowRowActionDTO,
            null
          )
          .then((response) => {
            dispatch(setIsMapOnFlyOpen({ value: false }));
            //SHEP-373: Replaced with new alert and close pop-up window
            dispatch(
              createAlert({
                status: response.data.alert,
                message:
                  "Successfully created Cross Reference, Workflow was successfully requeued",
                alertType: "single",
              })
            );
            closeWorkflowStepsPopup();
            //SHEP-373: refresh workflow manager tab status with processing
            // Update status via Redux dispatch to custom updateWorkflowStatus reducer
            dispatch(
              updateWorkflowStatus({
                workflowRecordId: mapOnFlyInfo.crossReferenceWorkflowRecordId,
                status: getStatusLabel(statusTypes.Processing),
              })
            );
          })
          .catch((error) => {
            console.error("Error applying option: ", error);
          });
      });
  };
  const handleRefresh = () => {
    dispatch(clearPendingLocalChanges());
    fetchCatalogs();
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CatalogMappingGrids
          sourceConnection={mapOnFlyInfo.crossReferenceInfo.SourceConnection}
          targetConnection={mapOnFlyInfo.crossReferenceInfo.TargetConnection}
          descriptionField={"ItemDesc"}
          catalogType={"Item"}
          onSourceSearchTextChange={setSourceSearchValue}
          onTargetSearchTextChange={setTargetSearchValue}
          sourceGridLoaded={sourceGridLoaded}
          targetGridLoaded={targetGridLoaded}
        >
          <DetailDisplay />
        </CatalogMappingGrids>
        <MappedCrossReferencesGrid
          sourceConnection={mapOnFlyInfo.crossReferenceInfo.SourceConnection}
          targetConnection={mapOnFlyInfo.crossReferenceInfo.TargetConnection}
        />
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "right",
            alignItems: "center",
            padding: "5% 10% 5% 0",
          }}
        >
          <Button className="btn-primary map-on-fly-CTA" onClick={handleSave}>
            Save
          </Button>
          {!isAddCrossRef && (
            <Button
              className="btn-primary map-on-fly-CTA"
              onClick={handleSaveAndRequeue}
              disabled={!canRequeue}
            >
              Save & requeue
            </Button>
          )}
          <Button
            className="btn-primary map-on-fly-CTA"
            onClick={handleRefresh}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </>
  );
};
export default ItemCatalogTab;
